/**
 * The studio header's behavior half: reads the studio and who is signed in,
 * and works out which links exist and which is current. Signed in, Services
 * and Events come from the client's own catalog, so a client at a studio whose
 * offerings are all members-only finds them (web-sign-in-checkout D5). Signing
 * in and out, the Messages button and the account menu are the account rules,
 * shared with the account piece (`useAccount`, web-studio-sites D15). Draws nothing.
 */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { SiteStudioDto } from '@telify/shared';

import { useSiteEvents, useSiteServices, useSiteStudio } from '../../api/site';
import { useWebSession } from '../../api/session';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { usePageMover } from '../../pageMover';
import { useAccount } from '../account/useAccount';
import {
  contract,
  type StudioHeaderActions,
  type StudioHeaderEvents,
  type StudioHeaderState,
  type StudioLink,
} from './contract';

export type StudioHeaderRuntime = PieceRuntime<typeof contract, StudioHeaderState, StudioHeaderActions>;

/** Only what the studio has: About when it has a story or a guide (the sections that carry #about). */
function linksFor(studio: SiteStudioDto | undefined, pathname: string, has: { services: boolean; events: boolean }): StudioLink[] {
  if (!studio) return [];
  const out: StudioLink[] = [];
  if (has.services) {
    out.push({ id: 'services', label: 'Services', href: '/#services', current: pathname === '/services' || pathname.startsWith('/services/') });
  }
  if (has.events) {
    out.push({ id: 'events', label: 'Events', href: '/events', current: pathname === '/events' || pathname.startsWith('/events/') });
  }
  if (studio.story?.trim() || studio.guide) out.push({ id: 'about', label: 'About', href: '/#about', current: false });
  return out;
}

export function useStudioHeader(input: PieceInput<StudioHeaderEvents> = {}): StudioHeaderRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const { pathname } = useLocation();
  const query = useSiteStudio();
  const studio = query.data;
  const signedIn = useWebSession().status === 'signedIn';
  const account = useAccount({ onEvent: emit, signIn: options.showSignIn });

  // Signed in, what this client can book; a guest's come with the studio's profile.
  const clientServices = useSiteServices();
  const clientEvents = useSiteEvents();
  const hasServices = signedIn ? (clientServices.data?.length ?? 0) > 0 : Boolean(studio?.hasServices);
  const hasEvents = signedIn ? (clientEvents.data?.pages[0]?.events.length ?? 0) > 0 : Boolean(studio?.hasEvents);

  const links = useMemo(
    () => linksFor(studio, pathname, { services: hasServices, events: hasEvents }),
    [studio, pathname, hasServices, hasEvents],
  );
  const status: StudioHeaderState['status'] = studio ? 'ready' : query.isError ? 'error' : 'loading';

  const actions = useMemo<StudioHeaderActions>(
    () => ({
      goHome: () => {
        emit('navigated', { linkId: 'home' });
        navigate('/');
      },
      open: (linkId) => {
        const link = links.find((l) => l.id === linkId);
        if (!link) return;
        emit('navigated', { linkId });
        navigate(link.href);
      },
      ...account.actions,
    }),
    [links, emit, navigate, account.actions],
  );

  return {
    state: {
      status,
      name: studio?.name ?? '',
      logoUrl: studio?.logoUrl ?? null,
      links,
      ...account.state,
    },
    actions,
    options,
  };
}

